import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { Router } from '@angular/router'; 
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { ConfirmationService } from "primeng/api";

import { TokenStorageService } from '../services/safeguard/token-storage.service';
import { AuthService } from '../services/safeguard/auth.service';
import { CustomLoggerService } from '../services/monitor/custom-logger.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  // Guards against a 401-from-a-stale-background-request kicking the user out
  // mid-navigation. Set when we issue the /login redirect, cleared on the next
  // refresh attempt so legitimate later sessions still get redirected.
  private loginRedirectIssued = false;

  constructor(
    private readonly router: Router,
    private readonly tokenStorageService: TokenStorageService, 
    private readonly confirmationService: ConfirmationService,
    private readonly customLoggerService: CustomLoggerService,
    private readonly authService: AuthService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // For those requests that do not use token based authentication or when logging-in
    // console.log('authIntercept: ' + req.url);
    // console.log('authIntercept req: ', req);
    if (req.url.includes('/auth/api/token')
       || req.url.includes('/auth/api/refresh-token')
       || req.url.includes('assets/config')
       || req.url.includes('/temp-pswd')
       || req.url.includes('/auth/users/find-user')
       || req.url.includes('/captcha/verify')
       || req.url.includes('/request-pswd-reset')
      ) {
      return next.handle(req);
    }

    let authReq = req;
    let authToken = this.tokenStorageService.getAuthToken();
    // console.log('authIntercept authToken: ' + authToken);
    if (authToken != null) {
      authReq = this.addTokenHeader(req, authToken);
    }
    // console.log('authIntercept authReq: ', authReq);
    return next.handle(authReq)
    .pipe(catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401
         && (error.error.message.includes('Token expired') || error.error.message.includes('__AUTH_TOKEN_EXPIRED__'))) {
        return this.handle401Error(authReq, next);
      }
      // console.log('authIntercept: ', error.status + ' ' + error.error.message);
      // // return catchError(this.errorProcessorService.handleError);
      //   this.confirmationService.confirm({
      //     message: error.error.message,
      //     key: "portal-user",   
      //     header: 'Exception Condition',
      //     icon: 'pi pi-exclamation-triangle',
      //     acceptLabel: "Ok",
      //     rejectVisible: false,
      //     acceptButtonStyleClass: "p-button-info wal-info  p-button-rounded",
      //     accept: () => {
      //           return;
      //         }
      //   });
      return throwError(error);
    }));
  }
  
  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.customLoggerService.logDebug('authIntercept: ', 'handle401Error(): ' + ' isRefreshing becomes true');
      this.isRefreshing = true;
      this.loginRedirectIssued = false;
      this.refreshTokenSubject.next(null);

      const userName = this.tokenStorageService.getCurrentUser().userName;
      const refreshToken = this.tokenStorageService.getRefreshToken();

      // console.log('authIntercept: ', 'handle401Error' + ' isRefreshing is already true');
      if (refreshToken)
        return this.authService.getRefreshedAuthToken(userName, refreshToken)
        .pipe(
          switchMap((authTokenResponse: any) => {
            this.isRefreshing = false;
            let authToken = authTokenResponse.accessToken;
            let refreshToken = authTokenResponse.refreshToken;
            this.tokenStorageService.setAuthToken(authToken);
            this.tokenStorageService.setRefreshToken(refreshToken);
            // Broadcast ONLY the access token to queued requests — they call
            // addTokenHeader() with whatever they receive here, so emitting the
            // refresh token first (its prior behavior) caused those queued
            // requests to retry with `Authorization: Bearer <refreshToken>`,
            // which the resource server rejects.
            this.refreshTokenSubject.next(authToken);
            return next.handle(this.addTokenHeader(request, authToken));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.redirectToLoginOnce();
            return throwError(err);
          })
        );
    }

    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({ headers: request.headers.set('Authorization', 'Bearer ' + token) });
  }

  // Runs signOut + session-expired dialog + /login navigation at most once per
  // refresh cycle. Without this, a stale background request that 401s while
  // the user is navigating triggers a redirect that interrupts the in-flight
  // navigation, producing the "briefly displays the new page then bounces"
  // behavior. Skips the navigate when the router is already targeting /login.
  private redirectToLoginOnce(): void {
    if (this.loginRedirectIssued) return;
    this.loginRedirectIssued = true;

    this.tokenStorageService.signOut();
    this.confirmationService.confirm({
      message: 'Your Walletron Portal session has expired.  Please login back into the web site, if needed.',
      header: 'Idle Session',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectVisible: false,
      acceptButtonStyleClass: 'p-button-info wal-info  p-button-rounded',
      accept: () => { /* noop */ },
      reject: () => { /* noop */ }
    });

    if (!this.router.url.startsWith('/login')) {
      this.router.navigate(['/login']);
    }
  }

}

export const authInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];